import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type QueryClient,
} from '@tanstack/react-query';
import {
  eraseFeedbackSubmission,
  fetchFeedbackAttachments,
  fetchFeedbackSubmission,
  fetchFeedbackSubmissions,
  fetchFeedbackSummary,
  isApiError,
  queryKeys,
  updateFeedbackSubmission,
  type FetchFeedbackParams,
  type UpdateFeedbackBody,
} from './api';

export function useFeedbackSubmissions(filters: FetchFeedbackParams) {
  return useQuery({
    queryKey: queryKeys.feedbackSubmissions(filters),
    queryFn: () => fetchFeedbackSubmissions(filters),
    placeholderData: keepPreviousData,
  });
}

export function useFeedbackSummary(
  filters: Omit<FetchFeedbackParams, 'status' | 'cursor' | 'limit' | 'sort'>,
) {
  return useQuery({
    queryKey: queryKeys.feedbackSummary(filters),
    queryFn: () => fetchFeedbackSummary(filters),
    placeholderData: keepPreviousData,
  });
}

export function useFeedbackSubmission(id: string) {
  return useQuery({
    queryKey: queryKeys.feedbackSubmission(id),
    queryFn: () => fetchFeedbackSubmission(id),
    enabled: !!id,
  });
}

export function useFeedbackAttachments(id: string) {
  return useQuery({
    queryKey: queryKeys.feedbackAttachments(id),
    queryFn: () => fetchFeedbackAttachments(id),
    enabled: !!id,
  });
}

export function useUpdateFeedbackSubmission(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateFeedbackBody) => updateFeedbackSubmission(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.feedbackSubmission(id) });
      // The list query key is parametrized by filters, so invalidate by the shared
      // prefix rather than trying to enumerate every filter combination in view.
      void qc.invalidateQueries({ queryKey: ['feedback-submissions'] });
    },
    // D26: a 409 (VERSION_CONFLICT) is retryable, not fatal — the caller
    // (feedback-detail.tsx) inspects the thrown ApiError's `.code` via
    // `isVersionConflict`/`isIllegalTransition` below and renders a reload
    // banner instead of a hard error state.
  });
}

export function useEraseFeedbackSubmission(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestRef: string) => eraseFeedbackSubmission(id, requestRef),
    onSuccess: () => {
      // Refresh the inbox and its counts. The erased record's own cache is dropped by
      // forgetErasedSubmission AFTER the page has navigated away: removing a query the mounted page still
      // observes makes it rebuild and refetch that query at once (a 404) and flash its loading state.
      void qc.invalidateQueries({ queryKey: ['feedback-submissions'] });
    },
  });
}

/** Drop an erased submission's cached detail and attachments. Call once its page is no longer mounted. */
export function forgetErasedSubmission(qc: QueryClient, id: string): void {
  qc.removeQueries({ queryKey: queryKeys.feedbackSubmission(id) });
  qc.removeQueries({ queryKey: queryKeys.feedbackAttachments(id) });
}

/** 502 ERASE_STORAGE_INCOMPLETE: a screenshot could not be deleted, so the request record was kept. */
export function isEraseStorageIncomplete(err: unknown): boolean {
  return isApiError(err) && err.code === 'ERASE_STORAGE_INCOMPLETE';
}

export function isVersionConflict(err: unknown): boolean {
  return isApiError(err) && err.code === 'VERSION_CONFLICT';
}

export function isIllegalTransition(
  err: unknown,
): err is { code: 'ILLEGAL_TRANSITION' } & Error & { body?: { from?: string; to?: string } } {
  return isApiError(err) && err.code === 'ILLEGAL_TRANSITION';
}

export function isUpstreamUnavailable(err: unknown): boolean {
  return isApiError(err) && err.code === 'UPSTREAM_UNAVAILABLE';
}

/**
 * A 502 UPSTREAM_UNAVAILABLE from the proxy, or a raw network failure (the
 * browser's fetch rejected before any response was received, so it never
 * reached api.ts's json() helper and has no ApiError shape at all) — both
 * mean "try again," not "your request was wrong."
 */
export function isRetryable(err: unknown): boolean {
  return isUpstreamUnavailable(err) || (err instanceof TypeError && !isApiError(err));
}
